import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Copy, Check, Maximize2 } from 'lucide-react';
import { useTheme } from '../../ThemeProvider';
import { renderMermaid, setMermaidTheme } from './renderMermaid';
import { MermaidViewerDialog } from './MermaidViewerDialog';

interface Props {
  code: string;
  onSettled?: () => void;
}

export function MermaidDiagram({ code, onSettled }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasRendered, setHasRendered] = useState(false);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { theme } = useTheme();
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHasRendered(false);
    setMermaidTheme(theme === 'dark' ? 'dark' : 'light');

    (async () => {
      try {
        const { svg } = await renderMermaid(code, idRef.current);
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svg;
        const svgEl = containerRef.current.querySelector('svg') as SVGSVGElement | null;
        if (svgEl) {
          let intrinsic = 0;
          const vb = svgEl.viewBox?.baseVal;
          if (vb && vb.width) intrinsic = vb.width;
          if (!intrinsic) {
            const mw = parseFloat(svgEl.style.maxWidth || '');
            if (!Number.isNaN(mw)) intrinsic = mw;
          }
          if (!intrinsic) {
            const wAttr = parseFloat(svgEl.getAttribute('width') || '');
            if (!Number.isNaN(wAttr)) intrinsic = wAttr;
          }
          svgEl.style.width = '100%';
          svgEl.style.height = 'auto';
          svgEl.style.maxWidth = intrinsic ? `${intrinsic}px` : '100%';
          svgEl.style.display = 'block';
          svgEl.style.margin = '0 auto';
        }
        setHasRendered(true);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to render diagram');
      } finally {
        if (!cancelled) onSettled?.();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, theme]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="group/mermaid relative my-6">
      {error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <strong>Error rendering diagram:</strong> {error}
          </div>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Show source
            </summary>
            <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-foreground">{code}</pre>
          </details>
        </div>
      ) : (
        <div className="mermaid-diagram-shell relative rounded-xl border border-border bg-card p-4 shadow-sm overflow-x-auto">
          {!hasRendered && <div className="h-48 w-full rounded-lg bg-muted animate-pulse" />}
          <div ref={containerRef} className={!hasRendered ? 'hidden' : 'flex justify-center'} />
          {hasRendered && (
            <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-background/80 px-1 py-1 shadow-sm backdrop-blur border border-border opacity-0 transition-opacity group-hover/mermaid:opacity-100 print:hidden [@media(hover:none)]:opacity-100">
              <button
                type="button"
                onClick={handleCopy}
                title="Copy source"
                className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(true)}
                title="Open fullscreen"
                className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
      <MermaidViewerDialog code={code} open={open} onOpenChange={setOpen} />
    </div>
  );
}

export default MermaidDiagram;
